import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { firestoreService } from '@/services/firestoreService';

interface Company {
  id: string;
  name: string;
  ticker: string;
  sector?: string;
  industry?: string;
  logo?: string;
}

export const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const allCompanies = await firestoreService.getDocuments<Company>('companies', [
        firestoreService.orderBy('name', 'asc'),
        firestoreService.limit(2000),
      ]);
      setCompanies(allCompanies);
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError(err instanceof Error ? err.message : 'Failed to load companies.');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.ticker.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <Card className="p-6">
            <p className="body-text text-red-600 mb-2">{error}</p>
            <Button variant="outline" onClick={loadCompanies}>Try again</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Companies</h1>
          <p className="body-text text-gray-600">
            Browse and analyze companies with AI-powered insights
          </p>
        </div>

        <div className="mb-6">
          <Input
            type="text"
            placeholder="Search companies by name or ticker..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {filteredCompanies.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600 mb-4">
                {searchTerm ? 'No companies found matching your search.' : 'No companies available yet.'}
              </p>
              {!searchTerm && (
                <p className="body-text text-sm text-gray-500">
                  Companies will appear here once they are added to the system.
                </p>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company) => (
              <Card key={company.id} hover>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="sub-header text-xl mb-1">{company.name}</h3>
                    <p className="body-text text-gray-600 font-mono">{company.ticker}</p>
                  </div>
                  {company.logo && (
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="w-12 h-12 rounded"
                    />
                  )}
                </div>
                {company.sector && (
                  <p className="body-text text-sm text-gray-500 mb-4">
                    {company.sector} {company.industry && `• ${company.industry}`}
                  </p>
                )}
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => navigate(`/companies/${company.ticker.toUpperCase()}`)}
                >
                  View Details
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

